import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase, authAPI, profileAPI, fetchProfileDirect } from '../lib/supabase'
import analyticsService from '../services/analyticsService'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)

  const clearAuthState = useCallback(async () => {
    try {
      // Clear Supabase session (this handles its own storage cleanup)
      await supabase.auth.signOut({ scope: 'local' })
      
      // Clear state
      setUser(null)
      setProfile(null)
      setSession(null)
    } catch (error) {
      console.error('Error clearing auth state:', error)
      // Even if signOut fails, still clear local state
      setUser(null)
      setProfile(null)
      setSession(null)
    }
  }, [])

  const loadUserProfile = useCallback(async (userId) => {
    try {
      // Use direct REST fetch — the SDK's .from() internally calls
      // _useSession() which queues behind any in-flight setSession(),
      // causing a deadlock right after OAuth callback.
      const data = await fetchProfileDirect(userId)
      
      if (!data) {
        console.log('Profile not found for user — new user, profile setup required')
        setProfile(null)
        return
      }
      
      setProfile(data)
    } catch (error) {
      console.error('Error loading profile:', error)
      // Don't clear auth state on transient errors — just leave profile null
      setProfile(null)
    }
  }, [])

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error getting session:', error)
          // Clear any stale auth data
          await clearAuthState()
          setLoading(false)
          return
        }
        
        setSession(session)
        setUser(session?.user ?? null)
        analyticsService.setUserId(session?.user?.id || null)
        
        if (session?.user) {
          await loadUserProfile(session.user.id)
        } else {
          setProfile(null)
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error)
        await clearAuthState()
      }
      
      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, !!session)
        
        setSession(session)
        setUser(session?.user ?? null)
        
        // Attach/detach user identity for analytics
        analyticsService.setUserId(session?.user?.id || null)
        
        if (session?.user) {
          // Signal loading BEFORE the async profile fetch so downstream
          // consumers (Navbar, ProfileSetup, etc.) know profile isn't ready yet.
          setLoading(true)
          await loadUserProfile(session.user.id)
        } else {
          setProfile(null)
        }
        
        setLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [loadUserProfile, clearAuthState])

  const signUp = async (email, password, userData) => {
    try {
      setLoading(true)
      const { data, error } = await authAPI.signUp(email, password, userData)
      
      if (error) throw error
      
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      setLoading(true)
      const { data, error } = await authAPI.signIn(email, password)
      
      if (error) throw error
      
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await authAPI.signInWithGoogle()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      
      // Sign out from Supabase (handles its own storage cleanup)
      const { error } = await authAPI.signOut()
      if (error) console.error('Supabase signOut error:', error)
      
      // Clear local state
      setUser(null)
      setProfile(null)
      setSession(null)
      
      return { error: null }
    } catch (error) {
      console.error('Sign out error:', error)
      
      // Even if Supabase sign out fails, clear local state
      setUser(null)
      setProfile(null)
      setSession(null)
      
      return { error }
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { data: null, error: new Error('No user logged in') }
    
    try {
      const { data, error } = await profileAPI.updateProfile(user.id, updates)
      
      if (error) throw error
      
      setProfile(data)
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    loadUserProfile,
    clearAuthState
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}